import {
  type UseMutateFunction,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import axios, { type AxiosError, type AxiosRequestConfig } from 'axios';
import useAxios from 'axios-hooks';
import { useContext, useEffect, useState } from 'react';
import { config } from '../config';
import { AppContext } from '../context/appContext';
import { AuthContext } from '../context/authContext';
import {
  type ApiEnvelop,
  GuestAuthContext,
  type SuccessEnvelop,
} from '../context/guestAuthContext';
import { LoadingContext } from '../context/loadingContext';
import { NotificationContext } from '../context/notificationContext';
import { PushNotificationContext } from '../context/pushNotificationContext';
import { ReservationsContext } from '../context/reservationContext';
import type { PropertyConfig } from '../interfaces';
import { endpoints } from '../mappers/urls';
import { useMessaging } from './useMessaging';

export interface ErrorEnvelop extends ApiEnvelop {
  error: {
    code: string;
    message: string;
    type: string;
  };
}

export interface Params {
  [key: string]: string | number | boolean | Date;
}

export interface RequestOptions<T = unknown> {
  params?: Params;
  payload?: T;
  authToken?: string;
  authGuestToken?: string;
  propertyId?: string;
}

export interface MutationsOptions {
  manual?: boolean;
  cacheResponse?: boolean;
  invalidateKeys?: (string | string[])[];
  ssr?: boolean;
  autoCancel?: boolean;
}

export interface Options {
  manual?: boolean;
  useCache?: boolean;
  ssr?: boolean;
  autoCancel?: boolean;
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean;
}

export interface MessageEvent<T> {
  name: string;
  callback: (data: T) => void;
}

export const useRequest = <R, T = unknown>(
  url: string,
  method: string,
  opts: RequestOptions<T>,
  hookOpts?: Options,
) => {
  const { params, payload, authToken, authGuestToken, propertyId } = opts;
  const reqConfig: AxiosRequestConfig<T> = {
    url,
    data: payload,
    baseURL: config.bookingEngine.endpoint,
    method,
  };

  if (params) {
    reqConfig.params = params;
  }

  if (authToken && !propertyId) {
    reqConfig.headers = { ...reqConfig.headers, authorization: authToken };
  }

  if (authToken && propertyId) {
    reqConfig.headers = {
      ...reqConfig.headers,
      authorization: authToken,
      'property-id': propertyId,
    };
  }

  if (authGuestToken) {
    reqConfig.headers = {
      ...reqConfig.headers,
      'reservation-token': authGuestToken,
    };
  }

  return useAxios<SuccessEnvelop<R>, T, ErrorEnvelop>(reqConfig, hookOpts);
};

export const useManualQueryRequest = <R, T = unknown>(
  url: string,
  opts: RequestOptions<T>,
  hookOpts?: Options & { manual?: boolean },
): [
  {
    data: SuccessEnvelop<R> | undefined;
    loading: boolean;
    error: Error | null;
  },
  (params?: Record<string, unknown>) => Promise<SuccessEnvelop<R>>,
] => {
  const { params: initialParams, authToken, authGuestToken, propertyId } = opts;
  const queryClient = useQueryClient();

  const headers: Record<string, string> = {};

  if (authToken) {
    headers.authorization = authToken;
  }

  if (authGuestToken) {
    headers['reservation-token'] = authGuestToken;
  }

  if (propertyId) {
    headers['property-id'] = propertyId;
  }

  const buildRequest = async (params?: Record<string, unknown>) => {
    const { data } = await axios.request<SuccessEnvelop<R>>({
      url,
      baseURL: config.bookingEngine.endpoint,
      method: 'get',
      params,
      headers,
    });

    return data;
  };

  const [data, setData] = useState<SuccessEnvelop<R> | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(!hookOpts?.manual);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = async (params?: Record<string, unknown>) => {
    setLoading(true);
    setError(null);
    try {
      const result = await queryClient.fetchQuery({
        queryKey: [url, 'get'],
        queryFn: () => buildRequest(params ?? initialParams),
      });

      setData(result);
      return result;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hookOpts?.manual) {
      fetchData();
    }
  }, []);

  return [{ data, loading, error }, fetchData];
};

export const useQueryRequest = <R, T = unknown>(
  url: string,
  opts: RequestOptions<T>,
  hookOpts?: Options,
) => {
  const { params, authToken, authGuestToken, propertyId } = opts;
  const reqConfig: AxiosRequestConfig<T> = {
    url,
    baseURL: config.bookingEngine.endpoint,
    method: 'get',
    params,
  };

  reqConfig.headers = reqConfig.headers ?? {};

  if (authToken) {
    reqConfig.headers = { authorization: authToken };
  }

  if (authGuestToken) {
    reqConfig.headers = { 'reservation-token': authGuestToken };
  }

  if (propertyId) {
    reqConfig.headers['property-id'] = propertyId;
  }

  const {
    data: response,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [url, params],
    queryFn: async () => {
      const { data } = await axios.request<SuccessEnvelop<R>>(reqConfig);
      return data;
    },
    staleTime: 1000 * 60 * 5,
    enabled: !hookOpts?.manual,
    refetchOnWindowFocus: hookOpts?.refetchOnWindowFocus ?? false,
    refetchOnMount: hookOpts?.refetchOnMount ?? true,
  });

  return { data: response, loading: isLoading, error, refetch };
};

export const useMutationRequest = <R, T = unknown>(
  url: string,
  method: 'post' | 'put' | 'delete' | 'patch',
  opts: RequestOptions<T>,
  hookOpts?: MutationsOptions,
): [
  {
    data: SuccessEnvelop<R> | undefined;
    loading: boolean;
    error: AxiosError<ErrorEnvelop> | null;
  },
  UseMutateFunction<SuccessEnvelop<R>, AxiosError<ErrorEnvelop>, T>,
] => {
  const { payload, authToken, authGuestToken, propertyId, params } = opts;
  const queryClient = useQueryClient();

  const reqConfig: AxiosRequestConfig<T> = {
    url,
    data: payload,
    baseURL: config.bookingEngine.endpoint,
    method,
  };

  if (authToken) {
    reqConfig.headers = { authorization: authToken };
  }

  if (params) {
    reqConfig.params = params;
  }

  if (authGuestToken) {
    reqConfig.headers = { 'reservation-token': authGuestToken };
  }

  if (propertyId) {
    if (reqConfig.headers) {
      reqConfig.headers['property-id'] = propertyId;
    } else {
      reqConfig.headers = { 'property-id': propertyId };
    }
  }

  const mutation = useMutation<SuccessEnvelop<R>, AxiosError<ErrorEnvelop>, T>({
    mutationFn: async (variables?: T) => {
      const { data } = await axios.request<SuccessEnvelop<R>>({
        ...reqConfig,
        data: variables || payload,
      });

      if (hookOpts?.cacheResponse) {
        queryClient.setQueryData([url, method], data);
      }

      return data;
    },
  });

  const { data, isPending, error, mutate } = mutation;

  return [{ data, loading: isPending, error }, mutate];
};

/**
 * Hook for executing a request that returns an ArrayBuffer (Binary data)
 */
export const useRequestBuffer = <R, T = unknown>(
  url: string,
  method: string,
  opts: RequestOptions<T>,
  hookOpts?: Options,
) => {
  const { params, payload, authToken, authGuestToken } = opts;

  const reqConfig: AxiosRequestConfig<T> = {
    url,
    data: payload,
    baseURL: config.bookingEngine.endpoint,
    method,
    responseType: 'arraybuffer',
  };

  if (params) {
    reqConfig.params = params;
  }

  if (authToken) {
    reqConfig.headers = { ...reqConfig.headers, authorization: authToken };
  }

  if (authGuestToken) {
    reqConfig.headers = {
      ...reqConfig.headers,
      'reservation-token': authGuestToken,
    };
  }

  return useAxios<ArrayBuffer, T, ArrayBuffer>(reqConfig, hookOpts);
};

export const useAuth = () => {
  return useContext(AuthContext);
};

export const usePushNotifications = () => {
  return useContext(PushNotificationContext);
};

export const useGuestAuth = () => {
  return useContext(GuestAuthContext);
};

export const useLoading = () => {
  return useContext(LoadingContext);
};

export const useNotifications = () => {
  return useContext(NotificationContext);
};

export const useAppContext = () => {
  return useContext(AppContext);
};
export const useReservationsContext = () => {
  return useContext(ReservationsContext);
};

export const useLocalStorage = <T,>(
  key: string,
  defaultValue?: T | null,
): [T | undefined | null, (newValue?: T | null) => void] => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const value = window.localStorage.getItem(key);

      if (value) {
        return JSON.parse(value) as T;
      }
      window.localStorage.removeItem(key);

      return defaultValue;
    } catch (err) {
      return defaultValue;
    }
  });

  const setValue = (newValue?: T | null): void => {
    if (newValue) {
      window.localStorage.setItem(key, JSON.stringify(newValue));
    } else {
      window.localStorage.removeItem(key);
    }

    setStoredValue(newValue);
  };

  return [storedValue, setValue];
};

export const useMessagingHook = <T,>(channelName: string, events: MessageEvent<T>[]) => {
  return useMessaging<T>(channelName, events);
};

/**
 * Hook to fetch property configuration including Stripe public key
 * @param propertyId - UUID of the property to fetch config for
 * @param options - Optional request options (authToken, authGuestToken, enabled flag)
 * @returns Property config data, loading state, error, and refetch function
 */
export const usePropertyConfig = (
  propertyId: string | undefined,
  options?: {
    authToken?: string;
    authGuestToken?: string;
    enabled?: boolean;
  },
) => {
  const { credentialsInfo } = useContext(AuthContext) || {};
  const enabled = (options?.enabled !== false && !!propertyId) ?? false;

  // Always provide a valid URL structure - use placeholder when propertyId is missing
  // The query won't execute when enabled=false (manual=true)
  const configUrl = propertyId
    ? `${endpoints.CONFIG}/details/${propertyId}`
    : `${endpoints.CONFIG}/details/placeholder`;

  // Determine which token to use - guest token takes precedence if provided
  const requestOptions: RequestOptions<unknown> = {
    propertyId: propertyId || undefined,
  };

  if (options?.authGuestToken) {
    // Guest portal context - use guest token
    requestOptions.authGuestToken = options.authGuestToken;
  } else {
    // Admin context - use Cognito token
    requestOptions.authToken = options?.authToken || credentialsInfo?.token;
  }

  const { data, loading, error, refetch } = useQueryRequest<PropertyConfig>(
    configUrl,
    requestOptions,
    {
      manual: !enabled,
      refetchOnWindowFocus: false,
    },
  );

  return {
    data: data?.result,
    loading: enabled ? loading : false,
    error: enabled ? error : null,
    refetch: enabled
      ? refetch
      : async () => {
          throw new Error('Cannot refetch: propertyId is required');
        },
  };
};
